import { ITheme } from 'office-ui-fabric-react';

import {
  ActionType,
  APIRetryBase,
  ComponentOverrides,
  DiagnosticsActionResult,
  DiagnosticsInitResponse,
  DiagnosticsProgressRequest,
  DiagnosticsProgressResponse,
  DiagnosticsStartRequest,
  ISAFActionExecutionResponse,
  ParamOrVar,
  SAFActionSchemaResponse,
  SAFContextVariable
} from '../models';

import { IIntentGraphStyles } from './intentGraph.types';
import { errorMessages } from '../models/validation.models';

// tslint:disable no-any
export interface IConfigObject {
  // force the config object to be flat
  // this eliminates a class of runtime error where attempting to access property on undefined.
  [key: string]: any;
}

export interface IEventObject {
  [key: string]: Function;
}

/**
 * A config updated listener could be called multiple times with Partial config data
 * when the config data is updated.
 *
 * If and only if when the return value of the listener is false, the data source should unregister
 * this listener and no longer call it with updates
 */
export type ConfigUpdatedListener<T> = (data: Partial<T>) => boolean | void;

/**
 * Implement a data source which can provide config data reactively
 *
 * This is part of adapter interface
 */
export interface IReactiveConfigurationDataSource<T extends IConfigObject> {
  /**
   * Register a listener
   *
   * register should return the configuration we have so far
   */
  register: (configUpdated?: ConfigUpdatedListener<T>) => Partial<T>;

  /**
   * Unregsiter a listener
   *
   * After a listener unregistered, it should no longer be called.
   */
  unregister: (configUpdated: ConfigUpdatedListener<T>) => void;
}
// tslint:disable no-any
// This should be the same as Microsoft.Online.BOX.Util.HostApp
export enum HostApps {
  GetHelp = 'GetHelp',
  SupportCentral = 'SupportCentral'
}

/**
 * Set of properties that the host can provide for customizing the behavior of the saf component
 */
export interface ISafDiagnosticsSettings {
  /**
   * multilineTextFieldRowCount for diagnostic form from host.
   */
  multilineTextFieldRowCount?: number;

  /**
   * diagnostic form titleStringLimit from the host.
   */
  titleStringLimit?: number;

  /**
   * diagnostic form buttonLabelLimit from the host.
   */
  buttonLabelLimit?: number;

  /**
   * safActionMaxRetries from the host.
   */
  safActionMaxRetries?: number;

  /**
   * diagnostics timeoutinseconds from the host.
   */
  timeoutInSeconds?: number;

  /**
   * diagnostics serviceTimerDelayInMilliseconds from the host.
   */
  serviceTimerDelayInMilliseconds?: number;

  /**
   * Hide progress bar
   */
  hideProgressStrings?: boolean;

  /**
   * Hide progress bar
   */
  hideProgressBar?: boolean;
}

export interface IRuntimeConfig extends IConfigObject {
  /**
   * SessionId from the host.
   */
  sessionId: string;

  /**
   * Name of the host application
   */
  hostAppName: string;

  /**
   * Prefix for logging
   */
  prefix?: string;

  /**
   * Current locale to use, in bcp-47 notation.
   */
  locale?: string;

  /**
   * Current Fluent UI theme object.
   */
  theme?: ITheme;

  /**
   * JSON Payload to process from the host environment.
   */
  payload?: string;

  /**
   * Timeout config for api calls, the heierarchy of timeout usage is if timeout from node is null then try using this
   * else use 60 seconds as timeout
   */
  timeoutInSeconds?: number;

  /**
   * Setting to enable scrolling to the last node if the component UX is outside the viewport
   */
  enableScrollToBottom?: boolean;

  /**
   * Setting to enable scrolling to the top of the previous rendered nodes question
   */
  enableScrollToTop?: boolean;

  /**
   * Setting to disable throwing validation error - to be used in demo envs
   */
  disableValidationException?: boolean;

  /**
   * className for scrollable host, to be used for scrollToTop feature
   */
  scrollRef?: string;

  /**
   * Default Error UI to be displayed in case of unexpected error
   */
  errorUI?: JSX.Element;

  /**
   * constants for inbuilt saf diagnostics handler
   */
  safDiagnosticsSettings?: ISafDiagnosticsSettings;

  /**
   * correlationId from the host.
   */
  correlationId?: string;

  /**
   * namespace for store
   */
  namespace?: string;

  /**
   * Setting to enable tracking of link clicks
   */
  logLinkClicks?: boolean;

  /**
   * Setting to enable using callbacks instead of async functions for SAF Apis
   */
  useCallBacksForSAFApis?: boolean;

  /**
   * List of Api names and the maximum number of retries for failures
   */
  apiRetries?: APIRetryBase[];

  /**
   * set rootStore to window object, window.rootStore
   */
  debugRootStore?: boolean;

  /**
   * Localized Resources Provided by Host
   */
  localizedResources?: Record<string, string>
}

export interface IRuntimeStaticConfig extends IRuntimeConfig {
  /**
   * custom styles for the component
   */
  customstyle?: (theme: ITheme) => Partial<IIntentGraphStyles>;
}

export interface IComponentProps {
  staticConfig: IRuntimeStaticConfig;
  callbackHandlers: Partial<IRuntimeHandlerConfig>;
}

export interface IConnectedComponentProps {
  callbackHandlers: Partial<IRuntimeHandlerConfig>;
  stylesFunc: (theme: ITheme) => Partial<IIntentGraphStyles>;
}

// NOTE: those names should be globally unique among all admin centers.
export enum RuntimeDataSources {
  Static = 'yoda.runtime.static'
}

export type LogHandlerType = (
  eventName: string,
  logLevel: string,
  sessionId?: string,
  message?: string,
  Adhocs?: any[]
) => void;
export type ContentRequestHandlerType = (
  contentType: string,
  contentId: string,
  parentDialogId?: string
) => Promise<string>;
export type NodeRenderedHandlerType = (
  metaId: string,
  title: string,
  team: string,
  nodeId: string,
  outcomeSignal: string,
  tags?: string,
  nodeType?: string,
  text?: string | null,
  nodeLogProperties?: string
) => void;
export type DiagnosticsActionHandlerType = (
  nodeId: string,
  actionType: ActionType,
  actionReference: string,
  timeoutInSecond?: number,
  parameters?: ParamOrVar[]
) => void;
export type SafDiagnosticsRendererType = (
  solutionType: string,
  solutionId: string
) => Promise<DiagnosticsInitResponse | null>;
export type PartnerSolutionRendererType = (
  solutionType: string,
  solutionId: string,
  nodeId: string,
  uId: string,
  dialogId: string
) => void;
export type GetSAFActionSchemaType = (actionId: string) => Promise<SAFActionSchemaResponse | null | void>;
export type ExecuteSAFActionType = (
  actionId: string,
  paramaters: object,
  contextVariables: SAFContextVariable[]
) => Promise<ISAFActionExecutionResponse | null | void>;
export type GetSafDiagnosticsComponentType = (diagnosticUId: string) => React.ReactNode;
export type GetComponentType = <T>(component: ComponentOverrides, ...args: any[]) => React.ComponentType<T>;
export type RaiseEventHandlerType = (eventName: string, eventArgs?: string, uId?: string) => void;
export type StartDiagnosticsType = (startRequest: DiagnosticsStartRequest) => Promise<DiagnosticsProgressResponse>;
export type ScrollToTopHandler = (lastNode?: HTMLElement) => void;
export type ScrollToBottomHandler = (lastNode?: HTMLElement) => void;
export type ValidationErrorHandlerType = (errors: errorMessages) => void;
export type GetDiagnosticsResultType = (
  progressRequest: DiagnosticsProgressRequest
) => Promise<DiagnosticsProgressResponse>;
export type IsUserSignedInHandlerType = () => boolean;
export type IsInitializedType = () => void;
export interface IRuntimeHandlerConfig extends IConfigObject {
  /** use enum LogEventName for eventName possible values
   *  use enum LogLevel for logLevel possible values
   *  sessionId: unique identifier for the current browser session
   *  message: any additional property needed for logs beyond the 6 Adhocs will be sent
   *           as Json string via message argument
   *  adhocs: The logger can only support 6 adhoc values, Adhocs[0], Adhocs[1], ..., Adhocs[5]
   *          Adhocs[0]: meta.id
   *          Adhocs[1]: meta.title
   *          Adhocs[2]: meta.supportAreaReference
   *          Adhocs[3]: node.id of the latest node rendered (currentNode)
   *          Adhocs[4]: node.type of currentNode
   *          Adhocs[5]: node.actionReference if node is of type ActionNode
   *
   * message = JSON.stringify({
   *                            message:LogMessageCodes.someValue
   *                            tags:"tagsValue"
   *                            Key3:"someValues3"
   *                            ....
   *                            ....
   *                          })
   * any additonal properties needed in a specific event need to be discussed and will be added to message argument
   */
  LogHandler?: LogHandlerType;

  /**
   * Handler used to call Alchemyapi to fetch resource content for contentTypes alchemyResource, c3, dmc and soc
   */
  ContentRequestHandler?: ContentRequestHandlerType;

  /**
   * Handler to provide information of the current node rendered
   */
  NodeRendered?: NodeRenderedHandlerType;

  /**
   * Handler to run daf diagnostics action
   */
  DiagnosticsActionHandler?: DiagnosticsActionHandlerType;

  /**
   * Handler to run saf diagnostics action
   */
  SafDiagnosticsRendered?: SafDiagnosticsRendererType;

  /**
   * Handler to pass partner solution Id and solution type
   */
  PartnerSolutionRendered?: PartnerSolutionRendererType;

  /**
   * Handler to run saf start action
   */
  StartDiagnostics?: StartDiagnosticsType;

  /**
   * Handler to run saf diagnostics progress action
   */
  GetDiagnosticsResult?: GetDiagnosticsResultType;

  /**
   * Handler to run saf diagnostics action
   */
  GetSafDiagnosticComponent?: GetSafDiagnosticsComponentType;

  /**
   * Handler to raiseEvent when a staticChoice contains raiseEventName property
   */
  RaiseEventHandler?: RaiseEventHandlerType;
  /**
   * Handler for intent graph validation errors
   */
  onValidationErrorHandler?: ValidationErrorHandlerType;
  /**
   * Handler for scroll to bottom feature
   */
  scrollToBottomHandler?: ScrollToBottomHandler;
  /**
   * Handler for scroll to top feature
   */
  scrollToTopHandler?: ScrollToTopHandler;
  /**
   * Handler to get SAF Action JsonSchema
   */
  GetSAFActionSchema?: GetSAFActionSchemaType;

  /**
   * Handler to fetch SAF Action execution result
   */
  ExecuteSAFAction?: ExecuteSAFActionType;
  /**
   * Call back to check if the user is signed in
   */
  IsUserSignedIn?: IsUserSignedInHandlerType;
  /**
   * Call back to notify host when Yoda is initialized
   */
  IsInitialized?: IsInitializedType;

  /**
   * callback to get replacement component
   */
  GetComponent?: GetComponentType;
}

export type ThemeChangedEventType = (theme: ITheme) => void;
export type DafDiagnosticsActionCompletedEventType = (nodeId: string, result: DiagnosticsActionResult) => void;
export type SafDiagnosticsActionCompletedEventType = () => void;
export type RaisedEventCompletedEventType = (eventName: string, succeeded: boolean, uId: string) => void;
export type SAFActionSchemaReturnedType = (result: SAFActionSchemaResponse) => void;
export type ExecuteSAFActionCompletedCompletedType = (result: ISAFActionExecutionResponse) => void;

export interface ICommunicationEvents extends IEventObject {
  /**
   * To trigger theme change in the component
   */
  onThemeChanged?: ThemeChangedEventType;

  /**
   * To return results back from the diagnostics action to the component
   */
  onDafDiagnosticsActionCompleted?: DafDiagnosticsActionCompletedEventType;

  /**
   * To return results back from the diagnostics action to the component
   */
  onSafDiagnosticsActionCompleted?: SafDiagnosticsActionCompletedEventType;

  /**
   * To return status of raised event
   */
  onRaisedEventCompleted?: RaisedEventCompletedEventType;

  /**
   * To return SAFAction diagnostics schema
   */
  onSAFActionSchemaReturned?: SAFActionSchemaReturnedType;

  /**
   * To return SAFAction diagnostics schema
   */
  onExecuteSAFActionCompleted?: ExecuteSAFActionCompletedCompletedType;
}

export const intentGraphDataSourceName = 'yoda.ds.intentGraph';
